import React, { useState, useEffect } from 'react';
import { ShieldCheckIcon, ExclamationTriangleIcon, ChartBarIcon, UserGroupIcon, CogIcon, BellIcon, EyeIcon, ClockIcon, UsersIcon, BuildingOfficeIcon, DocumentTextIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';

const AdminDashboard = () => {
  const [enterpriseStats, setEnterpriseStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalViolations: 0,
    criticalViolations: 0,
    complianceScore: 0,
    aiPlatforms: 0
  });

  const [recentViolations, setRecentViolations] = useState([]);
  const [userActivity, setUserActivity] = useState([]);
  const [systemHealth, setSystemHealth] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    try {
      // TODO: Replace with actual API calls
      // const response = await fetch('/api/admin/dashboard');
      // const data = await response.json();
      
      // Mock data for now
      setEnterpriseStats({
        totalUsers: 156,
        activeUsers: 142,
        totalViolations: 23,
        criticalViolations: 5,
        complianceScore: 89,
        aiPlatforms: 8
      });

      setRecentViolations([
        {
          id: 1,
          user: 'john.doe@company.com',
          violation: 'PII Detection',
          severity: 'high',
          timestamp: '2024-08-20T16:30:00Z',
          platform: 'ChatGPT',
          department: 'Engineering',
          status: 'pending'
        },
        {
          id: 2,
          user: 'jane.smith@company.com',
          violation: 'Trade Secret Exposure',
          severity: 'critical',
          timestamp: '2024-08-20T15:45:00Z',
          platform: 'Claude',
          department: 'Sales',
          status: 'investigating'
        },
        {
          id: 3,
          user: 'mike.johnson@company.com',
          violation: 'Policy Violation',
          severity: 'medium',
          timestamp: '2024-08-20T14:20:00Z',
          platform: 'Gemini',
          department: 'Marketing',
          status: 'resolved'
        }
      ]);

      setUserActivity([
        {
          id: 1,
          user: 'john.doe@company.com',
          action: 'AI Interaction',
          platform: 'ChatGPT',
          timestamp: '2024-08-20T16:45:00Z',
          compliance: 'clean'
        },
        {
          id: 2,
          user: 'jane.smith@company.com',
          action: 'Login',
          platform: 'Web',
          timestamp: '2024-08-20T16:30:00Z',
          compliance: 'clean'
        },
        {
          id: 3,
          user: 'mike.johnson@company.com',
          action: 'AI Interaction',
          platform: 'Claude',
          timestamp: '2024-08-20T16:15:00Z',
          compliance: 'clean'
        }
      ]);

      setSystemHealth({
        monitoringStatus: 'active',
        lastBackup: '2024-08-20T12:00:00Z',
        databaseStatus: 'healthy',
        apiStatus: 'operational',
        alertSystem: 'enabled'
      });

      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      setIsLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'resolved':
        return 'bg-green-100 text-green-800';
      case 'investigating':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getComplianceColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 75) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-sm text-gray-600">Enterprise compliance monitoring & management</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <BuildingOfficeIcon className="w-5 h-5 text-gray-400" />
                <span className="text-sm text-gray-600">Enterprise Admin</span>
              </div>
              <button className="p-2 text-gray-400 hover:text-gray-600">
                <BellIcon className="w-5 h-5" />
              </button>
              <button className="p-2 text-gray-400 hover:text-gray-600">
                <CogIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Enterprise Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <UsersIcon className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Users</p>
                <p className="text-2xl font-bold text-gray-900">{enterpriseStats.totalUsers}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <UserGroupIcon className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Active Users</p>
                <p className="text-2xl font-bold text-gray-900">{enterpriseStats.activeUsers}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Violations</p>
                <p className="text-2xl font-bold text-gray-900">{enterpriseStats.totalViolations}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Critical</p>
                <p className="text-2xl font-bold text-gray-900">{enterpriseStats.criticalViolations}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <ShieldCheckIcon className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Compliance</p>
                <p className={`text-2xl font-bold ${getComplianceColor(enterpriseStats.complianceScore)}`}>
                  {enterpriseStats.complianceScore}%
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Cog6ToothIcon className="w-6 h-6 text-indigo-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">AI Platforms</p>
                <p className="text-2xl font-bold text-gray-900">{enterpriseStats.aiPlatforms}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Recent Violations */}
          <div className="lg:col-span-2 bg-white rounded-lg shadow border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Recent Violations</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {recentViolations.map((violation) => (
                  <div key={violation.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSeverityColor(violation.severity)}`}>
                          {violation.severity}
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(violation.status)}`}>
                          {violation.status}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {new Date(violation.timestamp).toLocaleDateString()}
                      </span>
                    </div>
                    <h4 className="text-sm font-medium text-gray-900 mb-1">{violation.violation}</h4>
                    <div className="flex items-center justify-between text-sm text-gray-600">
                      <span>{violation.user}</span>
                      <span>{violation.platform}</span>
                      <span>{violation.department}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* System Health */}
          <div className="bg-white rounded-lg shadow border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">System Health</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Monitoring Status</span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {systemHealth.monitoringStatus}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Database</span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {systemHealth.databaseStatus}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">API Status</span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {systemHealth.apiStatus}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Alert System</span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {systemHealth.alertSystem}
                  </span>
                </div>
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-500">
                    Last Backup: {new Date(systemHealth.lastBackup).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* User Activity */}
        <div className="mt-8 bg-white rounded-lg shadow border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Recent User Activity</h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {userActivity.map((activity) => (
                <div key={activity.id} className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-blue-600">
                        {activity.user.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{activity.action}</p>
                    <p className="text-sm text-gray-500">{activity.user}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">{activity.platform}</span>
                    <span className="text-xs text-gray-400">
                      {new Date(activity.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-6">
          <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors">
            <UsersIcon className="w-5 h-5 inline mr-2" />
            Manage Users
          </button>
          <button className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium transition-colors">
            <DocumentTextIcon className="w-5 h-5 inline mr-2" />
            Generate Reports
          </button>
          <button className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-medium transition-colors">
            <CogIcon className="w-5 h-5 inline mr-2" />
            System Settings
          </button>
          <button className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-lg font-medium transition-colors">
            <BellIcon className="w-5 h-5 inline mr-2" />
            Alert Settings
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
